import { BaseSensorWebView } from '../../view/BaseSensorWebView';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../../ui/gzCheckbox';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../../ui/gzForm/validators/max-length.validator';
import { EditIntegrationConstants } from '../../../../editIntegration/EditIntegrationConstants';
import { RegexValidator } from '../../../../../ui/gzForm/validators/regex.validator';
import _ from 'lodash';
import { SensorEnabledStatus } from '../../../../common/types';
import { SensorType } from '../../../types/sensor-type';

export class O365SensorView extends BaseSensorWebView {
  public formGroup: FormGroup;
  public baseDom: {
    $wrapper: HTMLElement;
    $sensorSelectWrapper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $reuseDataWrapper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  };

  public dom: {
    $applicationId: GzFormFieldWebComponent;
    $tenantId: GzFormFieldWebComponent;
    $clientSecret: GzFormFieldWebComponent;
    $sensorSelectWrapper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $reuseDataWrapper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  };

  getDomElements(): void {
    this.baseDom = {
      $wrapper: this.shadow.querySelector('.form-wrapper'),
      $sensorSelectWrapper: this.shadow.querySelector('.sensor-select-wrapper'),
      $reuseDataWrapper: this.shadow.querySelector('.sensor-reuse-wrapper'),
    };
  }

  setTranslatedStrings(): void {
    this.dom.$applicationId.label = this.dependencies.translateFn('Application ID');
    this.dom.$tenantId.label = this.dependencies.translateFn('Tenant ID');
    this.dom.$clientSecret.label = this.dependencies.translateFn('Client Secret');
  }

  protected createFormGroup(): void {
    this.dom.$applicationId.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ApplicationId.MaxLength,
        this.dependencies.translateFn('The application ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ApplicationId.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The application ID cannot contain spaces')),
    ];
    this.dom.$tenantId.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.TenantId.MaxLength,
        this.dependencies.translateFn('The tenant ID must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.TenantId.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The tenant ID cannot contain spaces')),
    ];
    this.dom.$clientSecret.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(
        EditIntegrationConstants.ClientSecret.MaxLength,
        this.dependencies.translateFn('The Client Secret must have a maximum of {maxLength} characters.', {
          maxLength: EditIntegrationConstants.ClientSecret.MaxLength,
        })
      ),
      RegexValidator(/^\S*$/, this.dependencies.translateFn('The Client Secret cannot contain spaces')),
    ];
    this.formGroup = new FormGroup({
      application_id: this.dom.$applicationId,
      tenant_id: this.dom.$tenantId,
      client_secret: this.dom.$clientSecret,
    });

    if (this.dom.$reuseDataWrapper) {
      this.formGroup.addFormChild(['reuse_data'], this.dom.$reuseDataWrapper);
    }
  }

  protected setViewData(viewData) {
    if ('' === _.get(viewData, ['response', '_id'], '')) {
      //first init
      this.setEnabledAllFields(true);

      if (!this.baseDom.$reuseDataWrapper) {
        return;
      }

      this.baseDom.$reuseDataWrapper.Input.checked = true;
      this.baseDom.$sensorSelectWrapper.Input.checked = true;
      return;
    }

    const reuseData = _.get(viewData, ['response', 'specifics', 'reuseData'], false);
    if (this.baseDom.$reuseDataWrapper) {
      this.baseDom.$reuseDataWrapper.Input.checked = reuseData;
    }

    const enableStatus = _.get(viewData, ['response', 'enabledStatus'], true);
    const enabled = enableStatus === SensorEnabledStatus.ENABLED;
    this.baseDom.$sensorSelectWrapper.Input.checked = enabled;
    if (!enabled) {
      this.setEnableOnDataFields(enabled);
    }

    if (_.get(viewData, ['response', '_id'], false)) {
      this.formGroup.value = {
        application_id: _.get(viewData, ['response', 'configuration', 'application_id'], ''),
        tenant_id: _.get(viewData, ['response', 'configuration', 'tenant_id'], ''),
        client_secret: _.get(viewData, ['response', 'configuration', 'client_secret'], ''),
      };
    }
  }

  setBaseTranslatedStrings(sensorType: SensorType): void {
    super.setBaseTranslatedStrings(sensorType);
    const typeLabel =
      typeof this.translations.sensorNames[sensorType] !== 'undefined'
        ? this.translations.sensorNames[sensorType]
        : this.translations.unknownSensorTypeLabel;
    if (this.dom.$reuseDataWrapper) {
      this.dom.$reuseDataWrapper.Input.label = this.dependencies.translateFn('Use the same details for {sensorName}', {
        sensorName: typeLabel,
      });
    }
  }

  public resetData(): void {
    super.resetData();
    if (this.dom.$reuseDataWrapper) {
      this.dom.$reuseDataWrapper.disabled = true;
      this.dom.$reuseDataWrapper.Input.checked = true;
    }
  }
}
